import { useEffect, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Input, useDebounceCallback, DatePicker } from "@devhop/ui";
import { SearchIcon } from "lucide-react";
import { formatDateForInput } from "@/shared/lib/date-time";

export type MeetingSearchParams = {
  offset?: number;
  limit?: number;
  sort?: Array<{ field: string; dir: "asc" | "desc" }>;
  dateFrom?: string;
  dateTo?: string;
  search?: string;
};

type MeetingFilterProps = {
  searchParams: MeetingSearchParams;
  fromRoute: "/app/management/meetings";
};

export function MeetingFilter({
  searchParams,
  fromRoute,
}: MeetingFilterProps) {
  const nav = useNavigate({ from: fromRoute });
  const [dateFrom, setDateFrom] = useState(searchParams.dateFrom ?? "");
  const [dateTo, setDateTo] = useState(searchParams.dateTo ?? "");
  const [searchInput, setSearchInput] = useState(searchParams.search ?? "");

  useEffect(() => {
    setDateFrom(searchParams.dateFrom ?? "");
    setDateTo(searchParams.dateTo ?? "");
    setSearchInput(searchParams.search ?? "");
  }, [searchParams.dateFrom, searchParams.dateTo, searchParams.search]);

  const doNav = (
    opts: Partial<{ dateFromVal: string; dateToVal: string; searchVal: string }> = {},
  ) => {
    const df = opts.dateFromVal ?? dateFrom;
    const dt = opts.dateToVal ?? dateTo;
    const s = opts.searchVal ?? searchInput;
    nav({
      search: {
        offset: 0,
        limit: searchParams.limit ?? 20,
        sort: searchParams.sort,
        dateFrom: df.trim() || undefined,
        dateTo: dt.trim() || undefined,
        search: s.trim() || undefined,
      },
    });
  };

  const debouncedSearch = useDebounceCallback(
    (val: string) => doNav({ searchVal: val }),
    400,
  );

  return (
    <div className="mb-4 flex flex-col gap-2 px-2">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[200px] flex-1">
          <SearchIcon className="absolute top-1/2 left-2.5 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={searchInput}
            onChange={(e) => {
              setSearchInput(e.target.value);
              debouncedSearch(e.target.value);
            }}
            placeholder="ຄົ້ນຫາຫົວຂໍ້..."
            className="h-8 pl-8 sm:max-w-2xs"
          />
        </div>
        <DatePicker
          mode="range"
          value={{
            from: dateFrom ? new Date(dateFrom) : undefined,
            to: dateTo ? new Date(dateTo) : undefined,
          }}
          onChange={(range) => {
            const from: Date | undefined = Array.isArray(range)
              ? range[0]
              : range?.from;
            const to: Date | undefined = Array.isArray(range)
              ? range[1]
              : range?.to;
            const fromStr = from ? formatDateForInput(from) : "";
            const toStr = to ? formatDateForInput(to) : "";
            setDateFrom(fromStr);
            setDateTo(toStr);
            doNav({ dateFromVal: fromStr, dateToVal: toStr });
          }}
          placeholder="ເລືອກຊ່ວງວັນທີ"
          className="h-8 w-full sm:w-72"
        />
      </div>
    </div>
  );
}
